import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { RotateCcw, Play, Trophy } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { play, unlockAudio } from "@/lib/sfx";
import { http, safeGet } from "@/lib/api";
import { NEEDS_CAPTCHA, Turnstile } from "@/components/Turnstile";

const MEDALS = ["#f5b800", "#c0c7d1", "#d08b4a", "#38b6ff", "#2fb344"];

const Leaderboard = ({ board, t, mine }) => (
  <div className="mt-6 max-w-sm mx-auto rounded-[22px] sticker-sm bg-white p-4" data-testid="whack-leaderboard">
    <p className="font-display font-bold text-lg flex items-center gap-2"><Trophy className="w-5 h-5 text-[#f5b800]" /> {t.games.board}</p>
    {board.length === 0 ? (
      <p className="mt-2 text-sm font-bold text-ink/70" data-testid="whack-leaderboard-empty">{t.games.boardEmpty}</p>
    ) : (
      <ol className="mt-2 space-y-1.5">
        {board.map((r, i) => (
          <li key={r.id} className={`flex items-center gap-3 rounded-xl px-2 py-1.5 ${r.nickname === mine ? "bg-sun/40" : ""}`} data-testid={`whack-leaderboard-row-${i + 1}`}>
            <span className="grid place-items-center w-7 h-7 rounded-full border-2 border-ink font-extrabold text-sm" style={{ background: MEDALS[i] }}>{i + 1}</span>
            <span className="flex-1 font-extrabold truncate">{r.nickname}</span>
            <span className="font-display font-bold">{r.score} <span className="text-xs text-ink/60">{t.games.pts}</span></span>
          </li>
        ))}
      </ol>
    )}
  </div>
);

const FACES = ["an_lion", "an_elephant", "an_monkey", "an_panda", "an_giraffe", "an_frog"];
const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);
const newDeck = () => shuffle([...FACES, ...FACES]).map((f, i) => ({ id: i, f }));

export const MemoryGame = () => {
  const { t } = useI18n();
  const [deck, setDeck] = useState(newDeck);
  const [open, setOpen] = useState([]);
  const [done, setDone] = useState([]);
  const [moves, setMoves] = useState(0);
  const won = done.length === FACES.length;

  useEffect(() => {
    if (open.length !== 2) return undefined;
    const [a, b] = open.map((i) => deck[i]);
    const tm = setTimeout(() => {
      if (a.f === b.f) {
        play("sparkle");
        setDone((d) => [...d, a.f]);
      }
      setOpen([]);
    }, a.f === b.f ? 350 : 800);
    return () => clearTimeout(tm);
  }, [open, deck]);

  useEffect(() => {
    if (won) window.dispatchEvent(new Event("ananawi-applause"));
  }, [won]);

  const flip = (i) => {
    unlockAudio();
    if (open.length === 2 || open.includes(i) || done.includes(deck[i].f)) return;
    play("pop");
    if (open.length === 1) setMoves((m) => m + 1);
    setOpen((o) => [...o, i]);
  };
  const reset = () => {
    setDeck(newDeck());
    setOpen([]);
    setDone([]);
    setMoves(0);
  };

  return (
    <div data-testid="memory-game">
      <div className="flex items-center justify-between gap-3">
        <p className="font-bold text-ink/80">{t.games.memoryHint}</p>
        <span className="rounded-full bg-white sticker-sm px-3 py-1 text-sm font-extrabold" data-testid="memory-moves">{t.games.moves}: {moves}</span>
      </div>
      <p className="sr-only" aria-live="polite">{open.length ? open.map((i) => t.imgAlt.animals[deck[i].f] || "").join(", ") : ""}</p>
      <div className="mt-5 grid grid-cols-4 gap-2.5 sm:gap-3 max-w-md mx-auto">
        {deck.map((c, i) => {
          const shown = open.includes(i) || done.includes(c.f);
          return (
            <button key={c.id} onClick={() => flip(i)} className="relative aspect-square [perspective:600px]" data-testid={`memory-card-${i}`} aria-label={shown ? `${t.games.card} ${i + 1}: ${t.imgAlt.animals[c.f] || t.imgAlt.mascot}` : `${t.games.card} ${i + 1}`}>
              <motion.div className="absolute inset-0 [transform-style:preserve-3d]" animate={{ rotateY: shown ? 180 : 0 }} transition={{ duration: 0.35 }}>
                <div aria-hidden="true" className="absolute inset-0 rounded-2xl sticker-sm bg-pink grid place-items-center [backface-visibility:hidden]">
                  <span className="font-display font-bold text-3xl text-white">?</span>
                </div>
                <div aria-hidden="true" className={`absolute inset-0 rounded-2xl sticker-sm grid place-items-center [backface-visibility:hidden] [transform:rotateY(180deg)] ${done.includes(c.f) ? "bg-sun" : "bg-white"}`}>
                  <img src={`/brand/${c.f}.webp`} alt="" draggable={false} className="w-[92%] h-[92%] object-contain select-none" />
                </div>
              </motion.div>
            </button>
          );
        })}
      </div>
      {won && (
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="mt-5 text-center" data-testid="memory-win">
          <p className="font-display font-bold text-2xl">{t.games.win}</p>
        </motion.div>
      )}
      <div className="mt-5 flex justify-center">
        <button onClick={reset} className="btn-pop sticker-sm btn-pop-sm inline-flex items-center gap-2 rounded-full bg-sky text-white px-5 py-2.5 font-display font-semibold" data-testid="memory-reset-btn">
          <RotateCcw className="w-4 h-4" /> {t.games.again}
        </button>
      </div>
    </div>
  );
};

const DURATION = 30;
const BEST_KEY = "ananawi_whack_best";

export const WhackGame = () => {
  const { t } = useI18n();
  const [running, setRunning] = useState(false);
  const [left, setLeft] = useState(DURATION);
  const [score, setScore] = useState(0);
  const [hole, setHole] = useState(-1);
  const [hit, setHit] = useState(-1);
  const [best, setBest] = useState(() => Number(localStorage.getItem(BEST_KEY) || 0));
  const [over, setOver] = useState(false);
  const [final, setFinal] = useState(0);
  const [board, setBoard] = useState([]);
  const [nick, setNick] = useState(() => localStorage.getItem("ananawi_nick") || "");
  const [saved, setSaved] = useState(false);
  const [nickErr, setNickErr] = useState("");
  const [captcha, setCaptcha] = useState("");
  const [captchaKey, setCaptchaKey] = useState(0);
  useEffect(() => {
    safeGet("/games/whack/scores", []).then((d) => setBoard(Array.isArray(d) ? d : []));
  }, []);
  const qualifies = over && !saved && final > 0 && (board.length < 5 || final > (board[board.length - 1]?.score ?? 0));
  const saveScore = async (e) => {
    e.preventDefault();
    setNickErr("");
    try {
      const { data } = await http.post("/games/whack/scores", { nickname: nick, score: final, ...(NEEDS_CAPTCHA ? { turnstile_token: captcha } : {}) });
      setBoard(Array.isArray(data) ? data : []);
      localStorage.setItem("ananawi_nick", nick);
      setSaved(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setNickErr(d === "slow_down" ? t.games.slow : t.games.badNick);
      setCaptcha("");
      setCaptchaKey((k) => k + 1);
    }
  };
  const scoreRef = useRef(0);
  const timers = useRef([]);

  const clearAll = () => {
    timers.current.forEach((x) => { clearTimeout(x); clearInterval(x); });
    timers.current = [];
  };
  useEffect(() => () => clearAll(), []);

  const pop = useCallback(() => {
    setHit(-1);
    setHole((h) => {
      let n = Math.floor(Math.random() * 9);
      if (n === h) n = (n + 1) % 9;
      return n;
    });
    const speed = Math.max(520, 1100 - scoreRef.current * 35);
    timers.current.push(setTimeout(pop, speed));
  }, []);

  const start = () => {
    unlockAudio();
    clearAll();
    scoreRef.current = 0;
    setScore(0);
    setLeft(DURATION);
    setOver(false);
    setSaved(false);
    setRunning(true);
    pop();
    const iv = setInterval(() => {
      setLeft((l) => {
        if (l <= 1) {
          clearAll();
          setRunning(false);
          setHole(-1);
          setOver(true);
          setFinal(scoreRef.current);
          setBest((b) => {
            const nb = Math.max(b, scoreRef.current);
            localStorage.setItem(BEST_KEY, String(nb));
            return nb;
          });
          window.dispatchEvent(new Event("ananawi-applause"));
          return 0;
        }
        return l - 1;
      });
    }, 1000);
    timers.current.push(iv);
  };

  const whack = (i) => {
    if (!running || i !== hole || hit === i) return;
    play("boing");
    setHit(i);
    scoreRef.current += 1;
    setScore(scoreRef.current);
  };

  return (
    <div data-testid="whack-game">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-bold text-ink/80">{t.games.whackHint}</p>
        <div className="flex gap-2 text-sm font-extrabold">
          <span className="rounded-full bg-white sticker-sm px-3 py-1" data-testid="whack-score">{t.games.score}: {score}</span>
          <span className="rounded-full bg-white sticker-sm px-3 py-1" data-testid="whack-time">{t.games.time}: {left}s</span>
          <span className="rounded-full bg-sun sticker-sm px-3 py-1 inline-flex items-center gap-1" data-testid="whack-best" title={t.games.yourBest}><Trophy className="w-3.5 h-3.5" /> {best}</span>
        </div>
      </div>
      <Leaderboard board={board} t={t} mine={saved ? nick : ""} />
      <div className="mt-5 grid grid-cols-3 gap-3 max-w-sm mx-auto">
        {Array.from({ length: 9 }).map((_, i) => (
          <button key={i} onClick={() => whack(i)} className="relative aspect-square rounded-full bg-[#7a4b2a] border-[3px] border-ink overflow-hidden shadow-[inset_0_10px_0_rgba(0,0,0,0.25)]" data-testid={`whack-hole-${i}`} aria-label="hole">
            <motion.img
              src={hit === i ? "/brand/f_joy.webp" : "/brand/f_happy.webp"}
              alt={t.imgAlt.mascot}
              className="absolute inset-x-[10%] bottom-0 w-[80%] h-[85%] object-contain object-bottom"
              initial={false}
              animate={{ y: hole === i && hit !== i ? "0%" : "110%", scale: hit === i ? 1.1 : 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 22 }}
            />
          </button>
        ))}
      </div>
      {over && <p className="mt-4 text-center font-display font-bold text-2xl" data-testid="whack-over">{t.games.over} {t.games.score}: {score}</p>}
      {qualifies && (
        <form onSubmit={saveScore} className="mt-4 max-w-sm mx-auto rounded-[22px] sticker-sm bg-sun p-4" data-testid="whack-nick-form">
          <p className="font-display font-semibold">{t.games.qualify}</p>
          <div className="mt-2 flex gap-2">
            <input value={nick} onChange={(e) => setNick(e.target.value.slice(0, 12))} placeholder={t.games.nickPh} required minLength={2} maxLength={12} className="flex-1 min-w-0 rounded-full border-[2.5px] border-ink bg-white px-4 py-2 font-bold" data-testid="whack-nick-input" />
            <button disabled={NEEDS_CAPTCHA && !captcha} className="rounded-full bg-pink text-white border-[2.5px] border-ink px-4 font-extrabold disabled:opacity-60" data-testid="whack-nick-save">{t.games.saveNick}</button>
          </div>
          <div className="mt-2"><Turnstile key={captchaKey} onToken={setCaptcha} testId="whack-turnstile" /></div>
          {nickErr && <p className="mt-2 text-sm font-bold" data-testid="whack-nick-error">{nickErr}</p>}
        </form>
      )}
      <div className="mt-5 flex justify-center">
        <button onClick={start} disabled={running} className="btn-pop sticker-sm btn-pop-sm inline-flex items-center gap-2 rounded-full bg-leaf text-white px-6 py-2.5 font-display font-semibold disabled:opacity-60" data-testid="whack-start-btn">
          <Play className="w-4 h-4 fill-white" /> {over ? t.games.again : t.games.start}
        </button>
      </div>
    </div>
  );
};
